import {FC, useRef} from 'react';
import {Box} from '../Box';
import {DrawerProps as DProps} from '../Drawer';
import {NavItem} from './NavItem';
import {NavItemDrawer} from './NavItemDrawer';
import {StyledDrawerNav} from './base/StyledDrawerNav';
import {StackedNavList} from './base/StackedNavList';
import {StyledNavDrawer} from './base/StyledNavDrawer';
import {StyledNavItemButton} from './base/StyledNavItemButton';
import {navCssClassNames} from './navCssClassNames';
import {NavigationLinkOptions} from './types';
import clsx from 'clsx';
import ClickAwayListener from '@mui/material/ClickAwayListener';

export interface NavDrawerProps extends DProps {
    items?: NavigationLinkOptions[];
    footerItems?: NavigationLinkOptions[];
}

export const NavDrawer: FC<NavDrawerProps> = ({
    onClose, position, items, className, ...props
}) => {
    const dummyRootRef = useRef<HTMLDivElement>(null);
    const containerFunc = (): HTMLDivElement | null => dummyRootRef.current;

    const recursiveDrawerProps: DProps = {
        position,
        onClose,
        elevation: 0,
        container: containerFunc,
        className: clsx(className, navCssClassNames.drawer),
        variant: 'temporary' // [activate Modal Props](https://mui.com/material-ui/api/drawer/#import)
    };

    return (
        <>
            <div
                data-testid='dummy-root'
                ref={dummyRootRef}
            />

            <StyledNavDrawer
                {...props}
                {...recursiveDrawerProps}
                disablePortal
            >
                <ClickAwayListener
                    onClickAway={(event): void => {
                        onClose?.(event, 'backdropClick');
                    }}
                >
                    <Box
                        className={navCssClassNames.drawerBox}
                        display='flex'
                        flexDirection='column'
                    >
                        <StyledDrawerNav
                            as='nav'
                            className={navCssClassNames.drawerNav}
                        >
                            <StackedNavList className={navCssClassNames.drawerList}>
                                {items && items.map((item, itemIndex) => {
                                    if (item.subNavigationItems && item.subNavigationItems.length > 0) {
                                        return (
                                            <NavItem key={`${item.label }-${ itemIndex}`}>
                                                <NavItemDrawer
                                                    {...item}
                                                    {...recursiveDrawerProps}
                                                />
                                            </NavItem>
                                        );
                                    }
                                    return (
                                        <NavItem key={`${item.label }-${ itemIndex}`}>
                                            <StyledNavItemButton
                                                {...item}
                                                onClick={(clickEvent): void => {
                                                    item.onClick?.(clickEvent);
                                                    onClose?.(clickEvent, 'backdropClick');
                                                }}
                                                orientation='side'
                                                selected={item.selected}
                                            >
                                                {item.label}
                                            </StyledNavItemButton>
                                        </NavItem>
                                    );
                                })}
                            </StackedNavList>
                        </StyledDrawerNav>
                    </Box>
                </ClickAwayListener>
            </StyledNavDrawer>
        </>
    );
};

/**
 * NOTE:
 *
 * We're using a containerRef to pass to the Drawer component below
 * so when the NavDrawer is open, it can add [aria-expanded=true] to an
 * empty dummy component instead of the [document.body]. For the future,
 * this hacky work-around below is a solution a known bug
 * found [here](https://github.com/mui/material-ui/issues/19450).
 *
 * TODO:
 * We need to refactor this component to not use material-ui if we
 * want to get rid of this hack.
 * */
